import { RadioGroup, FormControlLabel, Radio, Button } from "@mui/material";
import { useTranslations } from "use-intl";
import { useFormik } from "formik";

import { CabinClass } from "@/app/lib/types/enums";
import type { TFlightFormValues } from "@/app/lib/types/types";
import { MAX_TRAVELLERS } from "@/app/lib/constants";
import PlusMinusButton from "../CounterButton";

type Props = {
  initialValues: TFlightFormValues;
  onConfirm: (values: TFlightFormValues) => void;
};

function PassengersSelection({ initialValues, onConfirm }: Props) {
  const t = useTranslations();

  const formik = useFormik({
    initialValues,
    onSubmit: onConfirm,
  });

  const maximumChild = MAX_TRAVELLERS - formik.values.adult;
  const maximumAdult = MAX_TRAVELLERS - formik.values.child;

  return (
    <div className="flex h-full w-full flex-col">
      <span className="mb-4 inline-block text-xs text-text-tertiary">
        {t("flights.search.travellers")}
      </span>
      <div className="flex justify-between py-2" aria-label="Adult">
        <div className="flex flex-col">
          <h4 className="font-medium text-text-primary">
            {t("flights.search.adult")}
          </h4>
          <span className="inline-block text-xs font-normal text-text-tertiary">
            {t("flights.search.twelveYearOldPlus")}
          </span>
        </div>
        <PlusMinusButton
          minValue={1}
          maxValue={maximumAdult}
          value={formik.values.adult}
          onChange={(value) => formik.setFieldValue("adult", value)}
        />
      </div>
      <div className="flex justify-between py-2" aria-label="Child">
        <div className="flex flex-col">
          <h4 className="font-medium text-text-primary">
            {t("flights.search.child")}
          </h4>
          <span className="inline-block text-xs font-normal text-text-tertiary">
            {t("flights.search.twoToTwelveYearOld")}
          </span>
        </div>
        <PlusMinusButton
          maxValue={maximumChild}
          value={formik.values.child}
          onChange={(value) => formik.setFieldValue("child", value)}
        />
      </div>
      <div className="flex justify-between py-2" aria-label="Infant">
        <div className="flex flex-col">
          <h4 className="font-medium text-text-primary">
            {t("flights.search.infant")}
          </h4>
          <span className="inline-block max-w-[200px] text-xs font-normal text-wrap text-text-tertiary">
            {t("flights.search.withoutSeat")}
          </span>
        </div>
        <PlusMinusButton
          maxValue={formik.values.adult}
          value={formik.values.infantWithoutSeat}
          onChange={(value) => formik.setFieldValue("infantWithoutSeat", value)}
        />
      </div>
      <span className="mt-4 mb-2 inline-block text-xs text-text-tertiary">
        {t("flights.cabinClass.cabinClass")}
      </span>
      <div>
        <RadioGroup
          aria-labelledby="cabin-class-radio-group"
          defaultValue="economy"
          name="cabinClass"
          value={formik.values.cabinClass}
          onChange={formik.handleChange}
        >
          <FormControlLabel
            className="flex justify-between py-2"
            value={CabinClass.ANY}
            control={<Radio />}
            label={t("flights.cabinClass.any")}
            labelPlacement="start"
            sx={{ m: 0 }}
          />
          <FormControlLabel
            className="flex justify-between py-2"
            value={CabinClass.ECONOMY}
            control={<Radio />}
            label={t("flights.cabinClass.economy")}
            labelPlacement="start"
            sx={{ m: 0 }}
          />
          <FormControlLabel
            className="m-0 flex justify-between py-2"
            value={CabinClass.PREMIUM_ECONOMY}
            control={<Radio />}
            label={t("flights.cabinClass.premiumEconomy")}
            labelPlacement="start"
            sx={{ m: 0 }}
          />
          <FormControlLabel
            className="m-0 flex justify-between py-2"
            value={CabinClass.FIRST_CLASS}
            control={<Radio />}
            label={t("flights.cabinClass.firstClass")}
            labelPlacement="start"
            sx={{ m: 0 }}
          />
          <FormControlLabel
            className="m-0 flex justify-between py-2"
            value={CabinClass.BUSINESS}
            control={<Radio />}
            label={t("flights.cabinClass.business")}
            labelPlacement="start"
            sx={{ m: 0 }}
          />
        </RadioGroup>
      </div>
      <Button
        onClick={() => formik.handleSubmit()}
        variant="contained"
        size="medium"
        fullWidth
        sx={{
          marginTop: "auto",
        }}
      >
        {t("confirm")}
      </Button>
    </div>
  );
}

export default PassengersSelection;
